/*
 * Copyright (c) 2024. Amazon.com, Inc. or its affiliates. All rights reserved.
 */

import {
    createPersistentItem,
    DeviceType,
    getUserAgentInfo,
    globalRef,
    type LogTarget,
} from '@amazon/vinyl-util'
import { createErrorHandler } from '@/errorHandler'
import type { ObjectSchema, ValueSchema } from '@amazon/vinyl-validation'
import { boolean, isOneOf, object } from '@amazon/vinyl-validation'
import { type StreamingQuality } from '@amzn/vinyl-amzn'
import { type MutableValue, MutableValueImpl } from '@amazon/vinyl-observable'
import { getDeviceConfig } from '@/device/deviceConfig'

export interface StreamingSettings {
    readonly streamingQuality: StreamingQuality
    readonly streamingQualityMobile: StreamingQuality
    readonly abrEnabled: boolean
    readonly loudnessNormalizationEnabled: boolean
}

const streamingQualityValidator: ValueSchema<StreamingQuality> = isOneOf(
    'HD',
    'STANDARD',
    'DATA_SAVER'
)

export const streamingSettingsValidator: ObjectSchema<StreamingSettings> = object({
    streamingQuality: streamingQualityValidator,
    streamingQualityMobile: streamingQualityValidator,
    abrEnabled: boolean(),
    loudnessNormalizationEnabled: boolean(),
})

const deviceConfig = getDeviceConfig()
const isDesktop = getUserAgentInfo().device?.type === DeviceType.DESKTOP
const streamingQualitySettingsLocal = createPersistentItem<StreamingSettings>({
    key: 'streamingQualitySettings',
    defaultValue: {
        streamingQuality: deviceConfig.streamingOptions.hdEnabled && isDesktop ? 'HD' : 'STANDARD',
        streamingQualityMobile: 'STANDARD',
        abrEnabled: true,
        loudnessNormalizationEnabled: false,
    },
    validator: streamingSettingsValidator.assert,
})

export type StreamingSettingsController = MutableValue<StreamingSettings | null>

export class StreamingSettingsControllerImpl
    extends MutableValueImpl<StreamingSettings | null>
    implements LogTarget
{
    readonly logPrefix = 'StreamingControllerImpl'
    private readonly errorHandler = createErrorHandler(this)

    constructor() {
        super(null)
        streamingQualitySettingsLocal
            .get()
            .then((value) => (this.value = value))
            .catch(this.errorHandler)
        this.onData(
            (value) =>
                value && void streamingQualitySettingsLocal.set(value).catch(this.errorHandler)
        )
    }
}

const streamingSettingsControllerRef = globalRef<StreamingSettingsController>(() => {
    return new StreamingSettingsControllerImpl()
})

export function getStreamingSettingsController() {
    return streamingSettingsControllerRef.value
}
